import { FC, useCallback, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import classNames from 'classnames'
import moment from 'moment'
import { Icon } from '@iconify/react'
import filePdfO from '@iconify/icons-fa/file-pdf-o'
import fileTextO from '@iconify/icons-fa/file-text-o'
import fileExcelO from '@iconify/icons-fa/file-excel-o'
import {
  PlusIcon,
  ChevronRightIcon,
  MagnifyingGlassIcon,
  TruckIcon,
} from '@heroicons/react/24/outline'
import broomIcon from '@iconify/icons-mdi/broom'
import {
  MainPage,
  PageTitle,
  PrimaryButton,
  SecondaryButton,
  FormSelect,
  FormSearch,
  FormText,
  FormDatePicker,
  PaginationFooter,
  GenericoOption,
} from '../../components'
import Dropdown from '../../components/Atoms/Dropdown'
import { filterGeneral } from '../../utils/filters'
import { ImportProgressBar } from '../../components/ShipmentBulkUpload/ImportProgressBar'
import { useBulkUploadProgress } from '../../hooks/useBulkUploadProgress'
import { useAppSelector } from '../../store/hooks'
import { getImportList } from '../../services/ShipmentBulkUploadServices'
import {
  headerStatusMeta,
  messageForCode,
  resolveHeaderStatusName,
} from '../../utils/shipmentBulkUploadStatus'
import { formatDate } from '../../utils/format'
import {
  exportTablePDF,
  exportTableXLSX,
  exportTableTXT,
} from '../../utils/exports'
import {
  ImportHeaderListItemDTO,
  ImportHeaderStatusName,
  ImportStatusId,
  hasManifest,
} from '../../interfaces/Import/ShipmentBulkUploadApi'
import { bulkUploadOwnerFilter } from '../../utils/bulkUploadOwnership'

const CREATE_ROUTE = '/shipment/bulk-upload'
const LIST_ROUTE = '/shipment/bulk-upload/list'
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100]

interface StatusOption {
  value?: number
  name: string
}
const STATUS_OPTIONS: StatusOption[] = [
  { value: undefined, name: 'Todos' },
  { value: ImportStatusId.HeaderInProcess, name: 'En proceso' },
  { value: ImportStatusId.HeaderFinalized, name: 'Finalizado' },
  {
    value: ImportStatusId.HeaderFinalizedWithErrors,
    name: 'Finalizado con errores',
  },
  { value: ImportStatusId.HeaderCancelled, name: 'Cancelado' },
  { value: ImportStatusId.HeaderDeleted, name: 'Eliminado' },
]

/**
 * Etiqueta del dueño de la carga: prioriza los nombres resueltos (sitio/cuenta) y cae
 * a los IDs si el backend no los devuelve. accountSiteID≠0 ⇒ crédito; account_id ⇒ COD.
 */
function ownerLabel(it: ImportHeaderListItemDTO): string {
  if (it.billing_account_name) return it.billing_account_name
  if (it.account_name) return it.account_name
  if (it.billing_account) return `Sitio ${it.billing_account}`
  if (it.account_id) return it.account_id
  return '–'
}

/**
 * Fila del dashboard. Si la importación está InProcess, pollea su progreso en vivo
 * (hook con enabled=isInProcess: las demás filas no pollean). Click → detalle.
 */
const ImportRow: FC<{ item: ImportHeaderListItemDTO }> = ({ item }) => {
  const navigate = useNavigate()
  // Mismo fallback por status_id que el badge: si no, la fila puede decir
  // "Finalizado" y el atajo de manifiesto no aparecer.
  const canonicalStatus = resolveHeaderStatusName(
    item.status_name,
    item.status_id
  )
  const isInProcess = canonicalStatus === ImportHeaderStatusName.InProcess
  const { progress } = useBulkUploadProgress(
    item.shipment_bulk_upload_header_id,
    {
      enabled: isInProcess,
    }
  )
  const isFinalized = canonicalStatus === ImportHeaderStatusName.Finalized
  const meta = headerStatusMeta(item.status_name, item.status_id)

  return (
    <tr
      onClick={() =>
        navigate(`/shipment/bulk-upload/${item.shipment_bulk_upload_header_id}`)
      }
      className="cursor-pointer hover:bg-gray-50"
    >
      <td className="px-3 py-2 font-mono text-xs text-gray-700">
        {item.bulk_upload_number}
      </td>
      <td className="px-3 py-2">
        <span
          className={`rounded-full px-2 py-0.5 text-xs font-semibold ${meta.badgeClass}`}
        >
          {meta.label}
        </span>
      </td>
      {/* <td className="px-3 py-2 text-gray-600">{ownerLabel(item)}</td> */}
      <td className="px-3 py-2">
        {isInProcess ? (
          <div className="w-40">
            <ImportProgressBar progress={progress} compact />
          </div>
        ) : (
          <span className="tabular-nums text-gray-700">
            {item.total_details}
          </span>
        )}
      </td>
      <td className="px-3 py-2 tabular-nums text-gray-500">
        {formatDate(item.start_date)}
      </td>
      <td className="px-3 py-2 text-gray-600">{item.creation_user_name}</td>

      {/* Manifiesto: estado si ya lo tiene, atajo si falta. El atajo lleva al
          detalle y abre el modal ahí — montar el flujo de despacho también acá
          duplicaría el host entero, y en el detalle el operador ve la carga que
          está por despachar. */}
      <td className="px-3 py-2">
        {hasManifest(item) ? (
          <span className="whitespace-nowrap text-xs text-green-700">
            ✓ {item.manifest_code ?? ''}{' '}
            <span className="text-gray-400">
              {formatDate(item.manifest_create_date)}
            </span>
          </span>
        ) : isFinalized ? (
          /* Botón SÓLIDO, no outline. En su primera versión era borde + texto en
             color sobre fondo transparente y sobre una fila de tabla se leía como
             deshabilitado. `PrimaryButton` además trae el color env-aware (azul
             en producción, rojo en desarrollo) y el focus ring del sistema. */
          <PrimaryButton
            className="!py-1 !px-2.5 !text-xs items-center gap-1 whitespace-nowrap"
            onClick={(e) => {
              // La fila entera navega al detalle; sin esto se dispararían las dos.
              e.stopPropagation()
              navigate(
                `/shipment/bulk-upload/${item.shipment_bulk_upload_header_id}?manifest=1`
              )
            }}
          >
            <TruckIcon className="h-3.5 w-3.5" />
            Crear
          </PrimaryButton>
        ) : (
          <span className="text-gray-400">–</span>
        )}
      </td>

      <td className="px-3 py-2 text-right">
        <ChevronRightIcon className="inline h-4 w-4 text-gray-400" />
      </td>
    </tr>
  )
}

const ShipmentBulkUploadList: FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const user = useAppSelector((s) => s.user)

  /**
   * Motivo por el que el detalle rebotó hasta acá (carga de otra línea de
   * facturación). Llega por `location.state` y no por `alertService` porque el
   * host de toasts (`<Alert />`) no está montado en la app: el mensaje sería
   * invisible.
   */
  const [deniedMessage, setDeniedMessage] = useState<string | null>(null)
  useEffect(() => {
    const message = (location.state as { deniedMessage?: string } | null)
      ?.deniedMessage
    if (!message) return
    setDeniedMessage(message)
    // Limpiar el history state: si no, un F5 vuelve a mostrar el aviso.
    navigate(LIST_ROUTE, { replace: true, state: null })
  }, [location.state, navigate])

  const [page, setPage] = useState(0) // 0-based (PaginationFooter); API es 1-based
  const [pageSize, setPageSize] = useState(10)
  const [items, setItems] = useState<ImportHeaderListItemDTO[]>([])
  const [count, setCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Filtros (aplicados al buscar)
  const [statusId, setStatusId] = useState<number | undefined>(undefined)
  // Texto visible del combo de estatus (FormSearch trabaja con strings). 'Todos' = sin filtro.
  const [statusSearch, setStatusSearch] = useState('Todos')
  // Distingue blur-tras-seleccionar de blur-sin-seleccionar (evita pisar la selección recién hecha).
  const statusJustSelected = useRef(false)
  const [importationNumber, setImportationNumber] = useState('')
  // Rango de fechas (Inicio de la carga). Por defecto desde el inicio del mes hasta
  // hoy; el operador ajusta el rango. Validación: Desde ≤ Hasta vía minDate/maxDate.
  const [dateFrom, setDateFrom] = useState<Date | null>(() =>
    moment().startOf('month').toDate()
  )
  const [dateTo, setDateTo] = useState<Date | null>(() => moment().toDate())
  const [applied, setApplied] = useState(0) // bump para disparar fetch

  const fetchList = useCallback(async () => {
    setLoading(true)
    // Dueño de la carga para acotar la consulta a la línea de facturación activa.
    // Misma regla que crear y que la guarda del detalle (`ownsBulkUpload`).
    const owner = bulkUploadOwnerFilter(user.client)
    const res = await getImportList({
      page: page + 1,
      page_size: pageSize,
      status_id: statusId,
      bulk_upload_number: importationNumber.trim() || undefined,
      start_date_from: dateFrom
        ? moment(dateFrom).format('YYYY-MM-DD')
        : undefined,
      start_date_to: dateTo ? moment(dateTo).format('YYYY-MM-DD') : undefined,
      ...owner,
    })
    setLoading(false)
    if (res.didError || !res.model) {
      // No filtramos por errorMessage crudo del backend (puede ser jerga técnica como
      // "Falta el encabezado de autenticación"). messageForCode mapea codes conocidos a
      // copy en español y cae a un mensaje genérico accionable para el operador.
      setError(
        messageForCode(
          res.code,
          'No se pudieron cargar las cargas masivas. Intentá de nuevo.'
        )
      )
      setItems([])
      setCount(0)
      return
    }
    setError(null)
    setItems(res.model.results)
    setCount(res.model.count)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, applied, pageSize])

  useEffect(() => {
    fetchList()
  }, [fetchList])

  const onSearch = () => {
    setPage(0)
    setApplied((a) => a + 1)
  }
  const onClear = () => {
    setStatusId(undefined)
    setStatusSearch('Todos')
    setImportationNumber('')
    setDateFrom(moment().startOf('month').toDate())
    setDateTo(moment().toDate())
    setPage(0)
    setApplied((a) => a + 1)
  }

  const onChangePageSize = (n: number) => {
    setPageSize(n)
    setPage(0)
  }

  // Export: arma columnas/filas de la PÁGINA actual (la lista es paginada en el
  // servidor). Para exportar más registros, subí el "Mostrar N registros".
  const EXPORT_COLUMNS = [
    '# Carga masiva',
    'Estatus',
    'Dueño',
    'Guías',
    'Inicio',
    'Operador',
    'Manifiesto',
  ]
  const buildExportRows = (): (string | number)[][] =>
    items.map((it) => [
      it.bulk_upload_number,
      headerStatusMeta(it.status_name, it.status_id).label,
      ownerLabel(it),
      it.total_details,
      formatDate(it.start_date),
      it.creation_user_name,
      hasManifest(it) ? (it.manifest_code ?? 'Sí') : '',
    ])

  return (
    <MainPage>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <PageTitle title="Carga Masiva" />
        <PrimaryButton
          onClick={() => navigate(CREATE_ROUTE)}
          className="h-10 text-sm -mt-6"
        >
          <PlusIcon className="mr-1 h-4 w-4" /> Nueva carga masiva
        </PrimaryButton>
      </div>

      {/* Rebote del detalle: carga masiva de otra línea de facturación */}
      {deniedMessage && (
        <div className="mb-4 flex items-start justify-between gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          <span>{deniedMessage}</span>
          <button
            type="button"
            aria-label="Cerrar aviso"
            onClick={() => setDeniedMessage(null)}
            className="font-semibold text-red-700 hover:text-red-900"
          >
            ✕
          </button>
        </div>
      )}

      {/* Filtros */}
      <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <div>
          <label className="mb-1 block text-xs text-gray-500">Estatus</label>
          <FormSearch
            id="import-status"
            name="import-status"
            value={statusSearch}
            options={STATUS_OPTIONS.map((o) => o.name)}
            unselect={statusId !== undefined}
            placeholder="Todos"
            className="!h-10"
            onChange={(e) => setStatusSearch(e.target.value)}
            onSelectOption={(option) => {
              statusJustSelected.current = true
              const found = STATUS_OPTIONS.find((o) => o.name === option)
              setStatusId(found?.value)
              setStatusSearch(found?.name ?? 'Todos')
            }}
            onChangeFocus={(focus) => {
              if (focus) {
                // Al enfocar, vaciar el texto para mostrar todas las opciones (si no, la
                // etiqueta seleccionada se usa como filtro y oculta el resto).
                setStatusSearch('')
              } else if (statusJustSelected.current) {
                statusJustSelected.current = false
              } else {
                // Blur sin seleccionar: restaurar la etiqueta del estatus vigente.
                setStatusSearch(
                  STATUS_OPTIONS.find((o) => o.value === statusId)?.name ??
                    'Todos'
                )
              }
            }}
            onFilter={filterGeneral}
            RenderOption={({ option }) => <GenericoOption text={option} />}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs text-gray-500">
            Nº carga masiva
          </label>
          <FormText
            id="import-number"
            name="import-number"
            value={importationNumber}
            placeholder="AUTO-…"
            onChange={(e) => setImportationNumber(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && onSearch()}
          />
        </div>
        <div>
          <FormDatePicker
            id="import-date-from"
            name="import-date-from"
            label="Desde"
            labelClassname="mb-1 !text-xs !font-normal !text-gray-500"
            useRange={false}
            maxDate={dateTo ?? undefined}
            value={{ startDate: dateFrom, endDate: dateFrom }}
            toggleClassName={(oldClassname) =>
              classNames(oldClassname, 'text-main-600')
            }
            onChange={(e) => {
              setDateFrom(e?.startDate ? moment(e.startDate).toDate() : null)
            }}
            configs={{ shortcuts: {} }}
          />
        </div>
        <div>
          <FormDatePicker
            id="import-date-to"
            name="import-date-to"
            label="Hasta"
            labelClassname="mb-1 !text-xs !font-normal !text-gray-500"
            useRange={false}
            minDate={dateFrom ?? undefined}
            value={{ startDate: dateTo, endDate: dateTo }}
            toggleClassName={(oldClassname) =>
              classNames(oldClassname, 'text-main-600')
            }
            onChange={(e) => {
              setDateTo(e?.startDate ? moment(e.startDate).toDate() : null)
            }}
            configs={{ shortcuts: {} }}
          />
        </div>
        <div className="flex items-end gap-2">
          <SecondaryButton
            className="h-10 flex-1 text-sm"
            title="Limpiar filtros"
            onClick={onClear}
          >
            <Icon icon={broomIcon} className="w-5 h-5" width="24" />
          </SecondaryButton>
          <PrimaryButton onClick={onSearch} className="h-10 flex-1 text-sm">
            Buscar <MagnifyingGlassIcon className="w-5 h-5 ml-2" />
          </PrimaryButton>
        </div>
      </div>

      {/* Toolbar: page-size + export */}
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center text-xs text-gray-600">
          <span>Mostrar</span>
          <div className="mx-1 inline">
            <FormSelect
              id="page-size"
              name="page-size"
              label=""
              selected={pageSize}
              options={PAGE_SIZE_OPTIONS}
              className="!h-8 !w-20 !px-2"
              optionString={(o) => o.toString()}
              onSelectOption={onChangePageSize}
            />
          </div>
          <span>registros</span>
        </div>
        <div className="flex items-center gap-2">
          <Dropdown
            align="left"
            label="Exportar"
            items={[
              {
                items: [
                  {
                    label: 'EXCEL',
                    icon: (props) => (
                      <Icon icon={fileExcelO} className={props.className} />
                    ),
                    onClick: () => {
                      if (!items.length) return
                      exportTableXLSX(
                        EXPORT_COLUMNS,
                        buildExportRows(),
                        'Carga Masiva',
                        'Carga Masiva'
                      )
                    },
                  },
                  {
                    label: 'PDF',
                    icon: (props) => (
                      <Icon icon={filePdfO} className={props.className} />
                    ),
                    onClick: () => {
                      if (!items.length) return
                      exportTablePDF(
                        user.user!,
                        'CARGA MASIVA',
                        EXPORT_COLUMNS,
                        buildExportRows(),
                        'Carga Masiva'
                      )
                    },
                  },
                  {
                    label: 'TXT',
                    icon: (props) => (
                      <Icon icon={fileTextO} className={props.className} />
                    ),
                    onClick: () => {
                      if (!items.length) return
                      exportTableTXT(
                        EXPORT_COLUMNS,
                        buildExportRows(),
                        'Carga Masiva'
                      )
                    },
                  },
                ],
              },
            ]}
            buttonClassName="bg-main-500 hover:bg-main-400 hover:border-main-400 text-white focus-visible:outline-main-400 !pt-1 !pb-1 !text-xs !ring-0 h-[32px] items-center"
            menuClassName="!left-0 !top-full !mt-1"
          />
        </div>
      </div>

      {/* Tabla */}
      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {error}{' '}
          <button onClick={fetchList} className="font-semibold underline">
            Reintentar
          </button>
        </div>
      ) : loading ? (
        <div className="animate-pulse space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-10 rounded bg-gray-100" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-300 bg-white p-10 text-center">
          <p className="text-sm font-medium text-gray-700">
            Aún no tienes cargas masivas
          </p>
          <p className="mt-1 text-xs text-gray-500">
            Crea tu primera carga masiva de guías para verla acá.
          </p>
          <PrimaryButton
            onClick={() => navigate(CREATE_ROUTE)}
            className="mt-4 text-sm"
          >
            <PlusIcon className="mr-1 h-4 w-4" /> Nueva carga masiva
          </PrimaryButton>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-left text-gray-500">
                <tr>
                  <th className="px-3 py-2 font-medium">Nº carga masiva</th>
                  <th className="px-3 py-2 font-medium">Estatus</th>
                  <th className="px-3 py-2 font-medium">Guías</th>
                  <th className="px-3 py-2 font-medium">Fecha de carga</th>
                  <th className="px-3 py-2 font-medium">Usuario de creación</th>
                  <th className="px-3 py-2 font-medium">Manifiesto</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((it) => (
                  <ImportRow
                    key={it.shipment_bulk_upload_header_id}
                    item={it}
                  />
                ))}
              </tbody>
            </table>
          </div>
          <PaginationFooter
            currentPage={page}
            totalItems={count}
            itemsPerPage={pageSize}
            onPageChange={(p) => setPage(p)}
          />
        </>
      )}
    </MainPage>
  )
}

export default ShipmentBulkUploadList
